import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Download, FileWarning, Search, X } from 'lucide-react';
import api from '../lib/api';
import { useEffectivePermissions } from '../hooks/useEffectivePermissions';
import { PassOnGate } from '../components/gsoc/PassOnGate';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';

const TZ = 'America/Chicago';
const fmt = (iso, opts) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: TZ, ...opts }) : '—');
const fmtDate = (iso) => fmt(iso, { month: 'short', day: 'numeric', year: 'numeric' });
const fmtTime = (iso) => fmt(iso, { hour: 'numeric', minute: '2-digit', second: '2-digit' });

const PRESETS = [
  { key: 'today', label: 'Today', days: 0 },
  { key: 'yesterday', label: 'Yesterday', days: 1, single: true },
  { key: '7d', label: 'Last 7 days', days: 6 },
  { key: '30d', label: 'Last 30 days', days: 29 },
  { key: 'month', label: 'This month', month: true },
];

const isoDay = (d) => d.toISOString().slice(0, 10);

const COLUMNS = [
  { key: 'entry_number', label: 'Entry #' },
  { key: 'submitted_at', label: 'Occurred' },
  { key: 'shift_label', label: 'Shift', sortable: false },
  { key: 'client_id', label: 'Client' },
  { key: 'site_id', label: 'Site' },
  { key: 'activity_type_id', label: 'Activity Type' },
  { key: 'employee_id', label: 'Operator' },
];

const delayLabel = (seconds) => {
  const m = Math.floor((seconds % 3600) / 60);
  const h = Math.floor(seconds / 3600);
  if (h >= 24) return `${Math.floor(h / 24)}d ${h % 24}h`;
  return h ? `${h}h ${m}m` : `${m}m`;
};

export default function GSOCLog() {
  return <PassOnGate><ActivityLog /></PassOnGate>;
}

function ActivityLog() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({ client_id: '', site_id: '', activity_type_id: '', shift: '', date_from: '', date_to: '', search: '' });
  const [searchDraft, setSearchDraft] = useState('');
  const [sort, setSort] = useState({ by: 'submitted_at', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ entries: [], total: 0, page_size: 50 });
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ clients: [], activity_types: [], shifts: [] });
  const [sites, setSites] = useState([]);
  const [detail, setDetail] = useState(null);
  const [exporting, setExporting] = useState(false);
  const perms = useEffectivePermissions();
  const canExport = perms.role === 'admin' || perms.can('gsoc', 'export');

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const params = { ...filters, sort_by: sort.by, sort_dir: sort.dir };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const res = await api.get('/gsoc/export', { params, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `GSOC_Activity_Log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Export failed');
    } finally { setExporting(false); }
  };

  useEffect(() => {
    Promise.all([api.get('/gsoc/form-options'), api.get('/gsoc/shifts')])
      .then(([o, s]) => setMeta({ clients: o.data.clients, activity_types: o.data.activity_types, shifts: s.data }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!filters.client_id) { setSites([]); return; }
    api.get('/gsoc/sites', { params: { client_id: filters.client_id } })
      .then(({ data }) => setSites(data)).catch(() => setSites([]));
  }, [filters.client_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, sort_by: sort.by, sort_dir: sort.dir, page, page_size: 50 };
      Object.keys(params).forEach((k) => { if (!params[k]) delete params[k]; });
      const { data } = await api.get('/gsoc/entries', { params });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load the activity log');
    } finally { setLoading(false); }
  }, [filters, sort, page]);

  useEffect(() => { load(); }, [load]);

  const applyPreset = (p) => {
    const now = new Date();
    if (p.month) {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      setFilters((f) => ({ ...f, date_from: isoDay(first), date_to: isoDay(now) }));
    } else if (p.single) {
      const d = new Date(now); d.setDate(d.getDate() - p.days);
      setFilters((f) => ({ ...f, date_from: isoDay(d), date_to: isoDay(d) }));
    } else {
      const from = new Date(now); from.setDate(from.getDate() - p.days);
      setFilters((f) => ({ ...f, date_from: isoDay(from), date_to: isoDay(now) }));
    }
    setPage(1);
  };

  const setSortKey = (key) => {
    setSort((s) => ({ by: key, dir: s.by === key && s.dir === 'desc' ? 'asc' : 'desc' }));
    setPage(1);
  };

  const totalPages = Math.max(1, Math.ceil(data.total / (data.page_size || 50)));
  const selectCls = 'h-9 px-2 text-sm rounded-md border border-line bg-surface text-fg';

  return (
    <div className="space-y-4" data-testid="gsoc-log-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <h1 className="page-title">GSOC Activity Log</h1>
        <div className="flex gap-2">
          {canExport && (
            <Button variant="outline" onClick={exportXlsx} disabled={exporting} data-testid="gsoc-export-btn">
              <Download className="w-4 h-4 mr-2" aria-hidden="true" />{exporting ? 'Building...' : 'Export to Excel'}
            </Button>
          )}
          <Button onClick={() => navigate('/gsoc/new')} data-testid="gsoc-new-entry-btn">New Entry</Button>
        </div>
      </div>

      <div className="rounded-lg border border-line bg-surface p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((p) => (
            <Button key={p.key} variant="outline" size="sm" onClick={() => applyPreset(p)} data-testid={`gsoc-preset-${p.key}`}>
              {p.label}
            </Button>
          ))}
          <Button variant="ghost" size="sm" data-testid="gsoc-clear-filters"
            onClick={() => { setFilters({ client_id: '', site_id: '', activity_type_id: '', shift: '', date_from: '', date_to: '', search: '' }); setSearchDraft(''); setPage(1); }}>
            <X className="w-3 h-3 mr-1" aria-hidden="true" />Clear
          </Button>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          <select className={selectCls} value={filters.client_id} data-testid="gsoc-filter-client"
                  onChange={(e) => { setFilters((f) => ({ ...f, client_id: e.target.value, site_id: '' })); setPage(1); }}>
            <option value="">All clients</option>
            {meta.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select className={selectCls} value={filters.site_id} disabled={!filters.client_id} data-testid="gsoc-filter-site"
                  onChange={(e) => { setFilters((f) => ({ ...f, site_id: e.target.value })); setPage(1); }}>
            <option value="">All sites</option>
            {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select className={selectCls} value={filters.activity_type_id} data-testid="gsoc-filter-type"
                  onChange={(e) => { setFilters((f) => ({ ...f, activity_type_id: e.target.value })); setPage(1); }}>
            <option value="">All activity types</option>
            {meta.activity_types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
          <select className={selectCls} value={filters.shift} data-testid="gsoc-filter-shift"
                  onChange={(e) => { setFilters((f) => ({ ...f, shift: e.target.value })); setPage(1); }}>
            <option value="">All shifts</option>
            {meta.shifts.map((s) => <option key={s.id} value={s.label}>{s.label}</option>)}
          </select>
          <input type="date" className={selectCls} value={filters.date_from} data-testid="gsoc-filter-from"
                 onChange={(e) => { setFilters((f) => ({ ...f, date_from: e.target.value })); setPage(1); }} />
          <input type="date" className={selectCls} value={filters.date_to} data-testid="gsoc-filter-to"
                 onChange={(e) => { setFilters((f) => ({ ...f, date_to: e.target.value })); setPage(1); }} />
          <div className="flex gap-1">
            <Input value={searchDraft} onChange={(e) => setSearchDraft(e.target.value)}
                   onKeyDown={(e) => { if (e.key === 'Enter') { setFilters((f) => ({ ...f, search: searchDraft })); setPage(1); } }}
                   placeholder="Search comments" className="h-9 w-48" data-testid="gsoc-search-input" />
            <Button size="sm" variant="outline" data-testid="gsoc-search-btn"
                    onClick={() => { setFilters((f) => ({ ...f, search: searchDraft })); setPage(1); }}>
              <Search className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-line bg-surface overflow-hidden">
        <div className="px-4 py-2 text-sm text-fg-muted border-b border-line" data-testid="gsoc-total-count">
          {loading ? 'Loading...' : `${data.total} ${data.total === 1 ? 'entry' : 'entries'}`}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-surface-sunken">
                {COLUMNS.map((c) => (
                  <th key={c.key} className="text-left px-4 py-3 font-medium text-fg-muted whitespace-nowrap">
                    {c.sortable === false ? c.label : (
                      <button type="button" onClick={() => setSortKey(c.key)} className="inline-flex items-center gap-1 hover:text-fg"
                              data-testid={`gsoc-sort-${c.key}`}>
                        {c.label}
                        {sort.by === c.key && (sort.dir === 'desc'
                          ? <ArrowDown className="w-3 h-3" aria-hidden="true" />
                          : <ArrowUp className="w-3 h-3" aria-hidden="true" />)}
                      </button>
                    )}
                  </th>
                ))}
                <th className="text-left px-4 py-3 font-medium text-fg-muted">Comments</th>
              </tr>
            </thead>
            <tbody>
              {data.entries.map((e) => (
                <tr key={e.id} onClick={() => setDetail(e.id)}
                    className="border-b border-line-subtle cursor-pointer hover:bg-[hsl(var(--hover-overlay))]"
                    data-testid={`gsoc-row-${e.entry_number}`}>
                  <td className="px-4 py-3 tabular-nums font-medium">
                    {e.entry_number}
                    {e.corrects_entry_id && (
                      <Badge className="ml-2 bg-warning-bg text-warning border border-warning-border">correction</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    {fmtDate(e.effective_at || e.submitted_at)}
                    <span className="text-fg-muted"> · {fmtTime(e.effective_at || e.submitted_at)}</span>
                    {e.is_delayed && (
                      <Badge className="ml-2 bg-info-bg text-info border border-info-border" data-testid={`gsoc-late-badge-${e.entry_number}`}>
                        logged {delayLabel(e.delay_seconds)} later
                      </Badge>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{e.shift_label}</td>
                  <td className="px-4 py-3">{e.client_name}</td>
                  <td className="px-4 py-3">{e.site_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{e.activity_type_label}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{e.operator_name}</td>
                  <td className="px-4 py-3 max-w-md truncate text-fg-secondary">{e.comments}</td>
                </tr>
              ))}
              {!loading && data.entries.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-fg-muted" data-testid="gsoc-empty">
                  No entries match these filters.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-line text-sm">
            <span className="text-fg-muted">Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} data-testid="gsoc-prev-page">Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)} data-testid="gsoc-next-page">Next</Button>
            </div>
          </div>
        )}
      </div>

      {detail && <EntryDetail entryId={detail} onClose={() => setDetail(null)} />}
    </div>
  );
}

function EntryDetail({ entryId, onClose }) {
  const navigate = useNavigate();
  const [entry, setEntry] = useState(null);

  useEffect(() => {
    api.get(`/gsoc/entries/${entryId}`).then(({ data }) => setEntry(data))
      .catch(() => { toast.error('Could not load the entry'); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entryId]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 sm:p-8 overflow-y-auto"
         style={{ backgroundColor: `hsl(var(--bg-overlay) / var(--bg-overlay-alpha))` }}
         onClick={onClose} data-testid="gsoc-detail-overlay">
      <div className="w-full max-w-2xl rounded-lg border border-line bg-surface shadow-lg" onClick={(e) => e.stopPropagation()}>
        {!entry ? (
          <div className="p-6 text-sm text-fg-muted">Loading...</div>
        ) : (
          <div className="p-6 space-y-4" data-testid="gsoc-detail">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-semibold text-fg">Entry #{entry.entry_number}</h2>
                <p className="text-sm text-fg-muted">{entry.activity_type_label} · {entry.shift_label} shift</p>
              </div>
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="gsoc-detail-close"><X className="w-4 h-4" /></Button>
            </div>

            <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
              <div><dt className="text-fg-muted">Client</dt><dd className="text-fg">{entry.client_name}</dd></div>
              <div><dt className="text-fg-muted">Site</dt><dd className="text-fg">{entry.site_name}</dd></div>
              <div><dt className="text-fg-muted">Operator</dt><dd className="text-fg">{entry.operator_name}</dd></div>
              <div><dt className="text-fg-muted">Shift</dt><dd className="text-fg">{entry.shift_label}</dd></div>
              <div>
                <dt className="text-fg-muted">Occurred</dt>
                <dd className="text-fg" data-testid="gsoc-detail-occurred">
                  {fmt(entry.effective_at || entry.submitted_at, { dateStyle: 'medium', timeStyle: 'medium' })}
                </dd>
              </div>
              <div>
                <dt className="text-fg-muted">Logged (server-stamped)</dt>
                <dd className="text-fg">{fmt(entry.submitted_at, { dateStyle: 'medium', timeStyle: 'medium' })}</dd>
              </div>
              <div><dt className="text-fg-muted">Form opened</dt><dd className="text-fg">{fmt(entry.started_at, { dateStyle: 'medium', timeStyle: 'medium' })}</dd></div>
            </dl>

            {entry.is_delayed && (
              <div className="rounded-md border border-info-border bg-info-bg px-3 py-2 text-sm" data-testid="gsoc-detail-delayed">
                <strong>Logged late.</strong> The operator recorded this {delayLabel(entry.delay_seconds)} after it happened.
                Both times are part of the entry's hash and cannot be changed.
              </div>
            )}

            <div>
              <p className="text-fg-muted text-sm">Operator comments / event details</p>
              <p className="mt-1 text-fg whitespace-pre-wrap">{entry.comments}</p>
            </div>

            {(entry.work_order_number || entry.account_number) && (
              <div className="flex flex-wrap gap-x-8 gap-y-1 text-sm" data-testid="gsoc-detail-refs">
                {entry.work_order_number && (
                  <div>
                    <p className="text-fg-muted">Work Order #</p>
                    <p className="text-fg font-medium">{entry.work_order_number}</p>
                  </div>
                )}
                {entry.account_number && (
                  <div>
                    <p className="text-fg-muted">Account #</p>
                    <p className="text-fg font-medium">{entry.account_number}</p>
                  </div>
                )}
                <p className="text-xs text-fg-muted self-end">Internal only — not shown on the client report.</p>
              </div>
            )}

            {entry.corrects && (
              <div className="rounded-md border border-warning-border bg-warning-bg px-3 py-2 text-sm" data-testid="gsoc-detail-corrects">
                This entry corrects entry #{entry.corrects.entry_number}.
              </div>
            )}
            {entry.corrected_by?.length > 0 && (
              <div className="rounded-md border border-info-border bg-info-bg px-3 py-2 text-sm" data-testid="gsoc-detail-corrected-by">
                A correction was filed for this entry: {entry.corrected_by.map((c) => `#${c.entry_number}`).join(', ')}. Both remain on the record.
              </div>
            )}

            <div className="pt-2 border-t border-line-subtle">
              <p className="text-xs text-fg-muted">Entry hash</p>
              <p className="text-xs font-mono break-all text-fg-secondary" data-testid="gsoc-detail-hash">{entry.content_hash}</p>
            </div>

            <div className="flex justify-between items-center pt-2">
              <p className="text-xs text-fg-muted">This record cannot be edited or deleted.</p>
              <Button variant="outline" onClick={() => navigate(`/gsoc/new?corrects=${entry.id}`)} data-testid="gsoc-file-correction-btn">
                <FileWarning className="w-4 h-4 mr-1" aria-hidden="true" />File Correction
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
